/**
 * EnergyControlFeatureGrid — Feature grid sitting ABOVE the EC checkout.
 *
 * Renders a 6-bullet feature grid that explicitly answers the visitor's
 * unspoken question "wat krijg ik voor mijn maandelijkse betaling?". Sits
 * boven de Phase1CommerceSection zodat de bezoeker waardepropositie ziet
 * vóór de checkout-knop. Pricing wordt apart getoond via de centrale
 * intro/regular labels (€ 9,95 eerste 3 mnd · daarna € 14,95) en niet
 * hard-coded in deze component.
 *
 * Hard rules (no leaks):
 *  - No automatic-control claims.
 *  - No savings guarantee.
 *  - The CTA delegates to the existing EnergyControlCheckoutButton so
 *    pricing, feature-flag and Stripe-503 handling stay centralised.
 *
 * Funnel tracking (May 2026 — Option C tracking fix):
 *  - ec_feature_grid_viewed     fires once when the grid actually renders.
 *  - ec_feature_grid_cta_clicked fires when the visitor clicks the EC v2
 *    checkout button (capture phase, so it lands before the button's own
 *    payment_clicked event). Payload is restricted to section / cta_label /
 *    cta_type / price / segment / intake_id. NEVER includes plain email.
 */
import { useEffect, useRef } from 'react';
import { Activity, Gauge, Boxes, ShieldAlert, FileSignature, Plug, Info } from 'lucide-react';
import EnergyControlCheckoutButton from '@/components/fase1/EnergyControlCheckoutButton';
import { useFeatureFlags } from '@/hooks/useFeatureFlags';
import { trackFunnelEvent } from '@/utils/tracking';

const SECTION = 'ec_feature_grid';
const SEGMENT = 'ec_v2_subscription';

const FEATURES = [
  { icon: Gauge,       text: 'Eigen Stroom Score' },
  { icon: Activity,    text: 'Maandelijkse bespaarkansen' },
  { icon: Boxes,       text: 'Product- en installatieadvies' },
  { icon: ShieldAlert, text: 'Noodstroomscenario bewaren' },
  { icon: FileSignature, text: 'Offertehulp via Powersure' },
  { icon: Plug,        text: 'Meer inzicht uit P1-meter en slimme meetproducten' },
];

export default function EnergyControlFeatureGrid({
  email = null,           // eslint-disable-line no-unused-vars  -- only passed to checkout button; NEVER to tracking
  intakeId = null,
  refCode = null,
}) {
  const { flags, loading } = useFeatureFlags();
  const viewedRef = useRef(false);
  const visible = !loading && !!flags.ec_v2_checkout_enabled;

  useEffect(() => {
    if (!visible || viewedRef.current) return;
    viewedRef.current = true;
    trackFunnelEvent('ec_feature_grid_viewed', {
      intake_id: intakeId || '',
      meta: {
        section: SECTION,
        segment: SEGMENT,
        feature_count: FEATURES.length,
        price: 9.95,
        intake_id: intakeId || '',
      },
    });
  }, [visible, intakeId]);

  if (!visible) return null;

  const ctaLabel = 'Start Energy Control · € 9,95 eerste 3 mnd · daarna € 14,95';

  const handleCtaClickCapture = () => {
    // Fires before the inner button's own payment_clicked event (capture
    // phase). We also fire product_cta_clicked so cross-component funnel
    // dashboards that aggregate generic CTA clicks see the EC v2 checkout
    // start. Payload is sanitized server-side; we keep it PII-free here.
    const meta = {
      section: SECTION,
      segment: SEGMENT,
      cta_label: ctaLabel,
      cta_type: 'ec_intro_checkout',
      price: 9.95,
      intake_id: intakeId || '',
    };
    trackFunnelEvent('ec_feature_grid_cta_clicked', { intake_id: intakeId || '', meta });
    trackFunnelEvent('product_cta_clicked', { intake_id: intakeId || '', meta });
  };

  return (
    <section
      className="rounded-2xl border border-primary/20 bg-white p-5 sm:p-7 shadow-sm"
      data-testid="ec-feature-grid"
    >
      <div className="text-[11px] uppercase tracking-wider font-semibold text-primary mb-2">
        Energy Control · maandabonnement
      </div>
      <h2
        className="text-2xl sm:text-3xl font-medium text-primary leading-snug"
        style={{ fontFamily: 'Outfit' }}
        data-testid="ec-feature-grid-title"
      >
        Wat krijgt u met Powersure Energy Control?
      </h2>

      <ul className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="ec-feature-grid-list">
        {FEATURES.map(({ icon: Icon, text }, i) => (
          <li
            key={i}
            className="flex items-start gap-3 bg-primary/5 border border-primary/10 rounded-xl p-3"
            data-testid={`ec-feature-${i + 1}`}
          >
            <span className="shrink-0 inline-flex items-center justify-center w-8 h-8 rounded-lg bg-primary/10 text-primary">
              <Icon className="w-4 h-4" />
            </span>
            <span className="text-sm sm:text-[15px] leading-relaxed text-foreground">{text}</span>
          </li>
        ))}
      </ul>

      <div className="mt-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div
          onClickCapture={handleCtaClickCapture}
          data-testid="ec-feature-grid-cta-wrap"
          className="w-full sm:w-auto"
        >
          <EnergyControlCheckoutButton
            enabled
            email={email}
            intakeId={intakeId}
            refCode={refCode}
            label={ctaLabel}
            className="w-full sm:w-auto"
          />
        </div>
        <p
          className="text-xs sm:text-[13px] text-muted-foreground leading-relaxed sm:max-w-[280px]"
          data-testid="ec-feature-grid-microcopy"
        >
          <Info className="inline w-3 h-3 mr-1 align-[-1px]" />
          Maandelijks opzegbaar. Geen automatische sturing zonder uw toestemming.
        </p>
      </div>
    </section>
  );
}
